<template lang="pug">
  .graphic(ref="container")
    .image(v-if="image")
      Spinner(v-if="loading" :size="16" :thickness="3")
      .observer
        lazy-component(@show="show")
          img(:src="image.src")
      figure(
        :style="{ height: `${height}px`, width: `${width}px`, 'background-image': `url(${image.loading})` }"
        :class="{ loaded }"
      )
        img(
          :src="image.src"
          :title="title"
          rel="noopener"
          :class="{ loaded }"
        )
      //- figcaption(v-if="title || subTitle")
      //-   p(v-if="title") {{ title }}
      //-   small(v-if="subTitle") {{ subTitle }}
</template>

<script>
import { get, trim, debounce } from '@/helpers';

export default {
    name: 'Graphic',
    props: {
        title: {
            type: String,
            required: true
        },
        data: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        loaded: false,
        loading: false,
        maxWidth: undefined,
        maxHeight: undefined
    }),
    watch: {
        image(data) {
            let img = new Image();
            img.src = data.src;
            img.onload = () => {
                this.loaded = true;
                setTimeout(() => (this.loading = false), 10);
            };
        }
    },
    computed: {
        width() {
            if (this.image.width && this.maxWidth) return Math.min(this.image.width, this.maxWidth);
            else return undefined;
        },
        height() {
            if (this.width && this.image.aspectRatio) return this.width / this.image.aspectRatio;
            else return undefined;
        },
        image() {
            const image = this.data;
            if (!image || !this.maxWidth || !this.maxHeight) return false;

            let fit = get(image, 'pivot.fit') || 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1';
            const parameters = {
                // ...get(fit.match(/x=(?<fx>[0-1]?\.?[0-9]*)/), 'groups'),
                // ...get(fit.match(/y=(?<fy>[0-1]?\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/z=(?<fz>[1-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/w=(?<w>[0-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/h=(?<h>[0-9]+\.?[0-9]*)/), 'groups')
            };
            let { w, h, fx, fy, fz } = parameters;
            // let { fx, fy, fz } = parameters;

            if (!fx) fx = 0.5;
            if (!fy) fy = 0.5;
            if (!fz) fz = 1;

            let width, height, aspectRatio;

            // get the actual image dimensions

            width = parseFloat(w) || image.width / fz;
            height = parseFloat(h) || image.height / fz;
            aspectRatio = width / height;

            const availableAspectRatio = this.maxWidth / this.maxHeight;

            // define the crop parameters
            // based on available space and user settings

            if (availableAspectRatio <= aspectRatio) {
                // scale by width
                width = Math.min(this.maxWidth, image.width);
                height = width / aspectRatio;
            } else {
                // scale by height
                height = Math.min(this.maxHeight, image.height);
                width = height * aspectRatio;
            }

            fit = `fit=crop&crop=focalpoint&x=${fx}&y=${fy}&z=${fz}&w=${width}&h=${height}`;

            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );

            return {
                src: `${storageRoute}/images/${image.id}/${image.name}?${fit}`,
                error: `${storageRoute}/images/${image.id}/${image.name}?${fit}&preview=greyscale`,
                loading: `${storageRoute}/images/${image.id}/${image.name}?${fit}&preview=blur`,
                width,
                height,
                aspectRatio
            };
        }
    },
    methods: {
        show() {
            // this.loaded = true;
            // setTimeout(() => this.loaded = true, 300)
        }
    },
    mounted() {
        setTimeout(() => {
            if (!this.loaded) this.loading = true;
        }, 100);

        const calculateMaxWidth = () => {
            if (this.$refs.container) {
                const style = getComputedStyle(this.$refs.container);

                this.maxWidth = this.$refs.container.clientWidth;
                this.maxWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);

                this.maxHeight = this.$refs.container.clientHeight;
                this.maxHeight -= parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
            }
        };

        window.addEventListener('resize', debounce(calculateMaxWidth, 300));
        calculateMaxWidth();
    }
};
</script>

<style lang="sass" scoped>
.graphic
  display: flex
  flex-grow: 1
  height: calc(100vh - 50px)
  justify-content: center
  align-items: center
  position: fixed
  right: 0
  top: 0

  .image
    display: flex
    justify-content: center

  figure
    @apply bg-gray-300 rounded shadow-md
    overflow: hidden
    background-position: center
    background-repeat: no-repeat
    background-size: cover
    img
      @apply rounded
      opacity: 0
      width: 100%
      transition: opacity .3s ease
      &.loaded
        opacity: 1

.observer
  display: absolute
  width: 0
  height: 0
  left: -300vw

.spinner-container
  position: absolute
  height: 100%
  width: 100%
  top: 0
  left: 0
  ::v-deep .spinner
    border-color: rgba(140,140,140,.2)
    border-top-color: var(--color-primary)
</style>
